import React, { createContext, useContext, useMemo } from 'react';

/**
 * Host capabilities needed by panel chrome (e.g. shifting the map aside while a panel is open).
 * `usePanelHost` never throws: without a `PanelHostProvider` it returns a no-op host.
 */
export interface PanelHost {
  setMapAside(aside: boolean): void;
}

interface PanelHostProviderProps {
  /** Called when a panel wants the map shifted aside (`true`) or restored (`false`). */
  onMapAsideChange: PanelHost['setMapAside'];
}

const NOOP_PANEL_HOST: PanelHost = { setMapAside: () => undefined };

export const PanelHostContext = createContext<PanelHost | undefined>(undefined);

/**
 * Takes typed props and builds the context value internally, so hosts don't repeat the `useMemo`
 * plumbing. Must not read page-specific contexts; host state arrives through props.
 */
export const PanelHostProvider: React.FC<React.PropsWithChildren<PanelHostProviderProps>> = ({
  onMapAsideChange,
  children,
}) => {
  const value = useMemo<PanelHost>(() => ({ setMapAside: onMapAsideChange }), [onMapAsideChange]);

  return React.createElement(PanelHostContext.Provider, { value }, children);
};

export const usePanelHost = (): PanelHost => useContext(PanelHostContext) ?? NOOP_PANEL_HOST;
